<template>
  <div class="article-mobile-container">
    <!-- 操作按钮区域，固定在右下角 -->
    <div class="mobile-action-buttons">
      <!-- 查询按钮 -->
      <el-button class="mobile-icon-btn" @click="showFilterDialog">
        <i class="el-icon-search"></i>
      </el-button>
      
      <!-- 统计按钮 -->
      <el-button class="mobile-icon-btn" @click="showStatisticsDialog">
        <i class="el-icon-data-analysis"></i>
      </el-button>
    </div>
    
    <!-- 标题栏 -->
    <div class="mobile-header" :class="{ 'header-hidden': !headerVisible }">
      <h1 class="page-title">文章管理</h1>
    </div>
    
    <!-- 文章卡片区域 -->
    <div class="article-cards-container" :class="{ 'header-hidden': !headerVisible }" v-loading="loading" @scroll="handleScroll">
      <div v-if="articleList.length === 0 && !loading" class="no-data">
        <div class="no-data-content">
          <i class="el-icon-document"></i>
          <span class="no-data-title">暂无文章</span>
          <p class="no-data-tips">
            没有找到符合条件的文章，请尝试调整筛选条件
          </p>
        </div>
      </div>
      
      <template v-else>
        <div 
          v-for="article in articleList" 
          :key="article.article_code"
          class="article-card"
          :class="{ 'article-card-expanded': expandedArticleCode === article.article_code }"
          @click="handleCardClick(article)"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
          :data-article-code="article.article_code"
        >
          <!-- 发布状态印章 -->
          <div class="publish-status-stamp">
            <div class="stamp-content" :class="{ 'published': article.is_push, 'hidden': !article.is_push }">
              {{ article.is_push ? '已发布' : '已隐藏' }}
            </div>
          </div>
          
          <!-- 文章基本信息 -->
          <div class="article-card-main">
            <!-- 文章名称区域 -->
            <div class="article-header" :class="{ 'with-top-indicator': article.is_top }">
              <div class="title-row">
                <span v-if="article.is_top" class="top-pill" aria-label="置顶">置顶</span>
                <h3 class="article-name">{{ article.article_name }}</h3>
              </div>
            </div>
            
            <!-- 分割线 -->
            <div class="article-divider"></div>
            
            <!-- 时间 + 标签 同行布局 -->
            <div class="article-meta-row">
              <!-- 文章标签（靠左，容器始终渲染，确保日期始终在最右） -->
              <div class="article-tags-container">
                <el-tag
                  v-for="tag in (article.article_tag ? article.article_tag.split(',') : [])"
                  :key="tag"
                  size="mini"
                  class="article-tag">
                  {{ tag }}
                </el-tag>
              </div>
              
              <!-- 发布时间（靠右） -->
              <div class="article-date">
                <i class="el-icon-date"></i>
                <span>{{ formatDate(article.create_time) }}</span>
              </div>
            </div>
            
            <!-- 文章类型三角形 -->
            <div class="article-type-triangle">
              <div class="triangle-background"
                   :style="{ 
                     'border-color': `transparent ${getArticleTypeStyle(article.article_type)} transparent transparent` 
                   }">
              </div>
              <span class="article-type-value">
                {{ article.article_type }}
              </span>
            </div>
          </div>
          
          <!-- 右滑操作区域 -->
          <div class="swipe-actions" :class="{ 'show': article.showActions }">
            <div class="action-buttons">
              <el-button
                class="action-btn"
                :class="article.has_password ? 'password-btn-filled' : 'password-btn-outline'"
                @click.stop="handlePassword(article)"
              >
                {{ article.has_password ? '查看密码' : '加密' }}
              </el-button>
              <el-button
                class="action-btn publish-btn"
                :type="article.is_push ? 'warning' : 'success'"
                @click.stop="handleTogglePush(article)"
              >
                {{ article.is_push ? '隐藏' : '发布' }}
              </el-button>
              <el-button
                class="action-btn top-btn"
                :type="article.is_top ? 'info' : 'primary'"
                @click.stop="handleToggleTop(article)"
              >
                {{ article.is_top ? '取消置顶' : '置顶' }}
              </el-button>
            </div>
          </div>
        </div>
      </template>
      
      <!-- 加载更多 -->
      <div v-if="hasMore && !loading && articleList.length > 0" class="load-more">
        <el-button type="text" @click="loadMore">加载更多</el-button>
      </div>
      <div v-if="loading && articleList.length > 0" class="loading-more">
        <i class="el-icon-loading"></i>
        <span>加载中...</span>
      </div>
      <div v-if="!hasMore && articleList.length > 0" class="no-more">
        <span>------已经到底啦------</span>
        <div class="copyright-info">
          <span class="mobile-copyright">Copyright © 2024 OIer之路 All rights reserved.<br>闽ICP备2025095302号-1</span>
        </div>
      </div>
    </div>
    
    <!-- 自定义统计信息弹窗 -->
    <div class="dialog-container" v-show="statisticsDialogVisible">
      <div class="dialog-mask" @click="statisticsDialogVisible = false"></div>
      <div class="custom-dialog stats-dialog" style="top: 10vh;">
        <div class="dialog-close" @click="statisticsDialogVisible = false">×</div>
        <div class="dialog-body statistics-dialog-body">
          <div class="dialog-content" style="background-color: #ffffff;">
            <h3 class="dialog-custom-title">文章统计</h3>
            <div class="mobile-statistics-grid">
              <!-- 文章总数 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatNumber(statistics.total_articles) }}</div>
                <div class="stat-label">
                  <i class="el-icon-document"></i>
                  文章总数
                </div>
              </div>
              
              <!-- 发布数量 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatNumber(statistics.published_count) }}</div>
                <div class="stat-label">
                  <i class="el-icon-view"></i>
                  发布数量
                </div>
              </div>
              
              <!-- 隐藏数量 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatNumber(statistics.hidden_count) }}</div>
                <div class="stat-label">
                  <i class="el-icon-hide"></i>
                  隐藏数量
                </div>
              </div>
              
              <!-- 置顶数量 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatNumber(statistics.top_count) }}</div>
                <div class="stat-label">
                  <i class="el-icon-top"></i>
                  置顶数量
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 自定义查询筛选弹窗 -->
    <div class="dialog-container" v-show="filterDialogVisible">
      <div class="dialog-mask" @click="filterDialogVisible = false"></div>
      <div class="custom-dialog" style="top: 10vh;">
        <div class="dialog-close" @click="filterDialogVisible = false">×</div>
        <div class="dialog-body filter-dialog-body">
          <div class="dialog-content filter-dialog-content" style="background-color: #ffffff;">
            <h3 class="dialog-custom-title filter-title">查询条件</h3>
            <el-form :model="filterForm" class="filter-form" size="small" label-width="70px">
              <el-form-item label="关键词">
                <el-input v-model="filterForm.keyword" placeholder="请输入关键词"></el-input>
              </el-form-item>
              
              <el-form-item label="文章名称">
                <el-input v-model="filterForm.article_name" placeholder="请输入文章名称"></el-input>
              </el-form-item>
              
              <el-form-item label="文章类型">
                <el-select v-model="filterForm.article_type" placeholder="请选择文章类型" clearable style="width: 100%">
                  <el-option
                    v-for="item in articleTypes"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value">
                  </el-option>
                </el-select>
              </el-form-item>
              
              <el-form-item label="标签">
                <el-input v-model="filterForm.article_tag" placeholder="请输入标签"></el-input>
              </el-form-item>
            </el-form>
            <div class="dialog-footer filter-footer">
              <el-button size="small" @click="handleReset">重置</el-button>
              <el-button size="small" type="primary" @click="handleSearch">查询</el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getArticles, updateArticlePushStatus, updateArticleTopStatus, getArticle, setArticlePassword, removeArticlePassword } from '@/api/article'

export default {
  name: 'ArticleMobile',
  data() {
    return {
      loading: false,
      articleList: [], // 当前页显示的数据
      currentPage: 1,
      pageSize: 20,
      total: 0,
      hasMore: true,
      sortProp: 'create_time',  // 默认按创建时间排序
      sortOrder: 'descending',  // 默认倒序
      
      // 统计数据
      statistics: {
        total_articles: 0,
        published_count: 0,
        hidden_count: 0,
        top_count: 0
      },
      
      // 弹窗控制
      statisticsDialogVisible: false,
      filterDialogVisible: false,
      
      // 展开的卡片
      expandedArticleCode: null,

      // 密码弹窗数据
      pwdDialogRow: null,
      pwdDialogHash: '',
      
      // 查询表单
      filterForm: {
        keyword: '',
        article_name: '',
        article_type: '',
        article_tag: ''
      },
      
      // 文章类型选项
      articleTypes: [
        { value: 'OI', label: 'OI' },
        { value: '题解', label: '题解' },
        { value: '游记', label: '游记' },
        { value: '生活', label: '生活' },
        { value: '学习', label: '学习' },
        { value: '科技', label: '科技' },
        { value: '情感', label: '情感' },
        { value: '游戏', label: '游戏' },
        { value: '随笔', label: '随笔' },
        { value: '其他', label: '其他' }
      ],
      
      // 触摸相关
      touchStartX: 0,
      touchStartY: 0,
      touchEndX: 0,
      touchEndY: 0,
      swipeThreshold: 50,
      ignoreEdgeSwipe: false,

      // 滚动状态控制
      lastScrollTop: 0,
      headerVisible: true,
      scrollThreshold: 10 // 滚动阈值，避免微小滚动触发
    }
  },
  created() {
    this.fetchArticleList()
    this.calculateStatistics()
  },
  
  methods: {
    shouldApplyPinnedSort() {
      return this.sortProp === 'create_time' && (this.sortOrder === 'descending' || !this.sortOrder)
    },
    parseTimeToTs(timeStr) {
      if (!timeStr) return 0
      const ts = new Date(String(timeStr).replace(/-/g, '/')).getTime()
      return Number.isFinite(ts) ? ts : 0
    },
    sortPinnedFirstByCreateTimeDesc(list) {
      return [...(list || [])].sort((a, b) => {
        const aTop = a?.is_top ? 1 : 0
        const bTop = b?.is_top ? 1 : 0
        if (aTop !== bTop) return bTop - aTop
        return this.parseTimeToTs(b?.create_time) - this.parseTimeToTs(a?.create_time)
      })
    },
    // 获取查询参数
    getQueryParams() {
      const params = {
        page: this.currentPage,
        size: this.pageSize,
        sort_prop: this.sortProp,
        sort_order: this.sortOrder
      };
      
      // 添加查询参数
      if (this.filterForm.keyword) {
        params.keyword = this.filterForm.keyword;
      }
      
      if (this.filterForm.article_name) {
        params.article_name = this.filterForm.article_name;
      }
      
      if (this.filterForm.article_type) {
        params.article_type = this.filterForm.article_type;
      }
      
      if (this.filterForm.article_tag) {
        params.article_tag = this.filterForm.article_tag;
      }
      
      return params;
    },
    
    // 获取文章列表
    async fetchArticleList(isLoadMore = false) {
      try {
        this.loading = true
        const params = this.getQueryParams()
        
        const res = await getArticles(params)
        if (res.code === 200) {
          if (isLoadMore) {
            this.articleList = [...this.articleList, ...res.data.items]
          } else {
            this.articleList = res.data.items || []
          }
          if (this.shouldApplyPinnedSort()) {
            this.articleList = this.sortPinnedFirstByCreateTimeDesc(this.articleList)
          }
          this.total = res.data.total
          this.hasMore = this.articleList.length < this.total
          
          // 为每个文章添加右滑状态
          this.articleList.forEach(article => {
            if (!article.hasOwnProperty('showActions')) {
              this.$set(article, 'showActions', false)
            }
          })
          
          // 重新计算统计
          this.calculateStatistics()
        } else {
          this.$message.error(res.msg || '获取文章列表失败')
        }
      } catch (error) {
        console.error('获取文章列表失败:', error)
        this.$message.error('获取文章列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 计算统计数据
    calculateStatistics() {
      this.statistics = {
        total_articles: this.articleList.length,
        published_count: this.articleList.filter(article => article.is_push).length,
        hidden_count: this.articleList.filter(article => !article.is_push).length,
        top_count: this.articleList.filter(article => article.is_top).length
      }
    },
    
    // 显示统计信息弹窗
    showStatisticsDialog() {
      this.statisticsDialogVisible = !this.statisticsDialogVisible;
    },
    
    // 显示查询弹窗
    showFilterDialog() {
      this.filterDialogVisible = !this.filterDialogVisible;
    },
    
    // 处理卡片点击
    handleCardClick(article) {
      console.log('卡片被点击:', article.article_name)
      // 跳转到文章查看页面
      this.$router.push(`/article/view/${article.article_code}`)
    },
    
    // 处理查询
    handleSearch() {
      this.currentPage = 1
      this.articleList = []
      this.fetchArticleList()
      this.filterDialogVisible = false
    },
    
    // 重置查询条件
    handleReset() {
      this.filterForm = {
        keyword: '',
        article_name: '',
        article_type: '',
        article_tag: ''
      }
    },
    
    // 加载更多
    loadMore() {
      if (!this.hasMore || this.loading) return
      
      this.currentPage++
      this.fetchArticleList(true)
    },
    
    // 处理滚动事件，实现自动加载更多
    handleScroll(event) {
      const container = event.target
      const scrollHeight = container.scrollHeight
      const scrollTop = container.scrollTop
      const clientHeight = container.clientHeight
      
      // 检测滚动方向并控制头部横幅显示
      const deltaY = scrollTop - this.lastScrollTop
      
      if (Math.abs(deltaY) > this.scrollThreshold) {
        if (deltaY > 0) {
          // 向下滚动（内容向上移动），隐藏头部横幅
          this.headerVisible = false
        } else {
          // 向上滚动（内容向下移动），显示头部横幅
          this.headerVisible = true
        }
      }
      
      this.lastScrollTop = scrollTop
      
      // 当滚动到距离底部100px时，加载更多内容（仅在满足条件时）
      if (!this.loading && this.hasMore && scrollHeight - scrollTop - clientHeight < 100) {
        this.loadMore()
      }
    },
    
    // 触摸开始
    handleTouchStart(event) {
      const touch = event.touches[0]
      this.touchStartX = touch.clientX
      this.touchStartY = touch.clientY
      this.ignoreEdgeSwipe = this.touchStartX <= 25
    },
    
    // 触摸移动
    handleTouchMove(event) {
      const touch = event.touches[0]
      this.touchEndX = touch.clientX
      this.touchEndY = touch.clientY
    },
    
    // 触摸结束
    handleTouchEnd(event) {
      const deltaX = this.touchEndX - this.touchStartX
      const deltaY = this.touchEndY - this.touchStartY
      
      // 只处理明显的水平滑动
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > this.swipeThreshold) {
        // 左边缘右滑交给系统返回
        if (this.ignoreEdgeSwipe && deltaX > 0) return
        const articleCode = event.currentTarget.getAttribute('data-article-code')
        const article = this.articleList.find(item => item.article_code === articleCode)
        if (article) {
          // 左滑展开操作按钮，右滑收起
          if (deltaX < 0) {
            this.articleList.forEach(item => {
              if (item.article_code !== articleCode) this.$set(item, 'showActions', false)
            })
            this.$set(article, 'showActions', true)
          } else {
            this.$set(article, 'showActions', false)
          }
        }
      }
    },
    
    // 切换发布/隐藏状态
    async handleTogglePush(article) {
      const action = article.is_push ? '隐藏' : '发布';
      try {
        await updateArticlePushStatus(article.article_code, !article.is_push);
        this.$message.success(`${action}成功`);
        article.is_push = !article.is_push;
        // 重新计算统计
        this.calculateStatistics();
        // 隐藏操作按钮
        this.$set(article, 'showActions', false);
      } catch (error) {
        console.error(`${action}文章失败：`, error);
        this.$message.error(`${action}文章失败`);
      }
    },
    
    // 切换置顶/取消置顶状态
    async handleToggleTop(article) {
      const action = article.is_top ? '取消置顶' : '置顶';
      try {
        await updateArticleTopStatus(article.article_code, !article.is_top);
        this.$message.success(`${action}成功`);
        article.is_top = !article.is_top;
        if (this.shouldApplyPinnedSort()) {
          this.articleList = this.sortPinnedFirstByCreateTimeDesc(this.articleList)
        }
        // 重新计算统计
        this.calculateStatistics();
        // 隐藏操作按钮
        this.$set(article, 'showActions', false);
      } catch (error) {
        console.error(`${action}文章失败：`, error);
        this.$message.error(`${action}文章失败`);
      }
    },

    // 密码管理按钮点击
    handlePassword(article) {
      if (article.has_password) {
        // 有密码，显示查看密码弹窗
        this.showPasswordDialog(article)
      } else {
        // 无密码，显示设置密码弹窗
        this.showSetPasswordDialog(article)
      }
      // 隐藏操作按钮
      this.$set(article, 'showActions', false)
    },

    // 显示设置密码弹窗
    showSetPasswordDialog(article) {
      this.$prompt('请输入文章密码', '设置文章密码', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputType: 'password',
        inputValidator: (value) => {
          if (!value) return '密码不能为空'
          return true
        }
      }).then(async ({ value }) => {
        // 确认密码
        this.$prompt('请再次输入密码', '确认密码', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputType: 'password',
          inputValidator: (val) => {
            if (!val) return '请再次输入密码'
            if (val !== value) return '两次输入密码不一致'
            return true
          }
        }).then(async ({ value: confirmValue }) => {
          try {
            await setArticlePassword(article.article_code, confirmValue)
            this.$message.success('密码设置成功')
            this.fetchArticleList()
          } catch (error) {
            console.error('设置密码失败：', error)
            this.$message.error('设置密码失败')
          }
        }).catch(() => {})
      }).catch(() => {})
    },

    // 显示查看密码弹窗
    async showPasswordDialog(article) {
      try {
        const response = await getArticle(article.article_code)
        const articleData = response.data
        this.pwdDialogRow = article
        this.pwdDialogHash = articleData.article_password || ''

        const h = this.$createElement

        // 密码行
        const pwdRow = h('div', { class: 'pwd-row' }, [
          h('span', { class: 'pwd-label' }, '密码：'),
          h('span', {
            class: 'pwd-value',
            style: 'font-size:14px;font-family:monospace;letter-spacing:1px;color:#303133;flex:1;overflow:hidden;text-overflow:ellipsis;',
            domProps: { id: 'pwd-text' }
          }, '••••••••••••••••'),
          h('i', {
            class: 'el-icon-view pwd-icon-btn',
            style: 'cursor:pointer;font-size:16px;color:#909399;',
            attrs: { id: 'pwd-toggle' },
            on: {
              click: () => {
                const textEl = document.getElementById('pwd-text')
                const iconEl = document.getElementById('pwd-toggle')
                if (!textEl || !iconEl) return
                const isShowing = textEl.textContent !== '••••••••••••••••'
                if (isShowing) {
                  textEl.textContent = '••••••••••••••••'
                  iconEl.style.color = '#909399'
                } else {
                  textEl.textContent = this.pwdDialogHash
                  iconEl.style.color = '#16988C'
                }
              }
            }
          }),
          h('i', {
            class: 'el-icon-document-copy pwd-icon-btn',
            style: 'cursor:pointer;font-size:16px;color:#909399;margin-left:6px;',
            on: { click: () => {
              if (!this.pwdDialogHash) return
              navigator.clipboard.writeText(this.pwdDialogHash).then(() => {
                this.$message.success('密码已复制')
              }).catch(() => {
                this.$message.error('复制失败')
              })
            }}
          })
        ])

        // 按钮行
        const btnRow = h('div', { class: 'pwd-btn-row' }, [
          h('el-button', {
            props: { type: 'primary', size: 'small' },
            on: {
              click: () => {
                this.$msgbox.close()
                this.showChangePasswordDialog()
              }
            }
          }, '修改密码'),
          h('el-button', {
            props: { type: 'danger', size: 'small', plain: true },
            on: {
              click: () => {
                this.$msgbox.close()
                this.showRemovePasswordDialog()
              }
            }
          }, '取消密码')
        ])

        this.$msgbox({
          title: '文章密码',
          message: h('div', { class: 'pwd-msgbox-content' }, [pwdRow, btnRow]),
          confirmButtonText: '关闭',
          cancelButtonText: '取消',
          closeOnClickModal: false
        }).then(() => {}).catch(() => {})
      } catch (error) {
        console.error('获取文章信息失败：', error)
        this.$message.error('获取文章信息失败')
      }
    },

    // 修改密码流程
    showChangePasswordDialog() {
      this.$prompt('请输入原密码', '验证原密码', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputType: 'password',
        closeOnClickModal: false
      }).then(async ({ value }) => {
        this.$prompt('请输入新密码', '设置新密码', {
          confirmButtonText: '下一步',
          cancelButtonText: '取消',
          inputType: 'password',
          closeOnClickModal: false
        }).then(async ({ value: newPassword }) => {
          this.$prompt('请再次输入新密码', '确认新密码', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            inputType: 'password',
            closeOnClickModal: false,
            inputValidator: (val) => {
              if (!val) return '请再次输入新密码'
              if (val !== newPassword) return '两次输入密码不一致'
              return true
            }
          }).then(async ({ value: confirmPassword }) => {
            try {
              await setArticlePassword(this.pwdDialogRow.article_code, confirmPassword)
              this.$message.success('密码修改成功')
              this.fetchArticleList()
            } catch (error) {
              console.error('修改密码失败：', error)
              this.$message.error('修改密码失败')
            }
          }).catch(() => {})
        }).catch(() => {})
      }).catch(() => {})
    },

    // 取消密码流程
    showRemovePasswordDialog() {
      this.$prompt('请输入原密码以确认取消密码保护', '取消密码保护', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputType: 'password',
        closeOnClickModal: false
      }).then(async ({ value }) => {
        try {
          await removeArticlePassword(this.pwdDialogRow.article_code, value)
          this.$message.success('密码已取消')
          this.fetchArticleList()
        } catch (error) {
          console.error('取消密码失败：', error)
          if (error.response?.data?.message === '密码错误') {
            this.$message.error('密码错误')
          } else {
            this.$message.error('取消密码失败')
          }
        }
      }).catch(() => {})
    },
    
    // 格式化日期
    formatDate(date) {
      if (!date) return '未设置'
      return date.split(' ')[0] // 只取日期部分
    },
    
    // 格式化数字
    formatNumber(value) {
      if (value === undefined || value === null) return '0'
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    },
    
    // 获取文章类型对应的样式
    getArticleTypeStyle(type) {
      const styleMap = {
        'OI': '#409EFF',        // Element UI 主蓝色
        '题解': '#67C23A',      // Element UI 成功绿色
        '游记': '#E6A23C',      // Element UI 警告黄色
        '生活': '#F56C6C',      // Element UI 危险红色
        '学习': '#8E44AD',      // 深紫色
        '科技': '#16A085',      // 深青色
        '情感': '#D35400',      // 深橙色
        '游戏': '#2980B9',      // 深蓝色
        '随笔': '#27AE60',      // 深绿色
        '其他': '#C0392B'       // 深红色
      }
      return styleMap[type] || ''
    }
  }
}
</script>

<style lang="scss" scoped>
.article-mobile-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f0f2f5;
  position: relative;
  overflow: hidden;
}

/* 移动端操作按钮 - 与App.vue中全局菜单按钮样式保持一致 */
.mobile-action-buttons {
  position: fixed;
  right: 15px;
  bottom: 135px; /* 距离底部的距离 */
  display: flex;
  flex-direction: column;
  gap: 15px; /* 按钮之间的垂直间距 */
  z-index: 2001;
  width: 40px; /* 限制容器宽度与按钮一致 */
  align-items: center; /* 确保按钮水平居中对齐 */
  padding: 0 !important;
  margin: 0 !important;
  
  /* 确保每个按钮容器没有额外的边距和内边距 */
  .el-button {
    margin: 0 !important;
    padding: 0 !important;
    box-sizing: border-box !important;
  }
}

.mobile-icon-btn {
  width: 40px !important; /* 与App.vue保持一致 */
  height: 40px !important; /* 与App.vue保持一致 */
  border-radius: 40px !important; /* 与App.vue保持一致 */
  background-color: rgba(255, 255, 255, 0.5) !important; /* 与App.vue保持一致 */
  color: #409eff !important;
  border: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  padding: 0 !important;
  margin: 0 !important; /* 确保没有外边距干扰对齐 */
  transition: all 0.3s ease !important;
  position: relative !important; /* 使用相对定位 */
  left: 0 !important; /* 确保没有左偏移 */
  
  i {
    font-size: 20px !important; /* 与App.vue保持一致 */
    margin: 0 !important; /* 确保图标没有外边距 */
    padding: 0 !important; /* 确保图标没有内边距 */
    display: inline-flex !important; /* 使用inline-flex布局 */
    justify-content: center !important; /* 水平居中 */
    align-items: center !important; /* 垂直居中 */
    width: 20px !important; /* 固定宽度 */
    height: 20px !important; /* 固定高度 */
  }
  
  &:hover, &:focus {
    background-color: rgba(255, 255, 255, 0.8) !important; /* 与App.vue保持一致 */
    transform: scale(1.05) !important;
  }
  
  &:active {
    background-color: rgba(255, 255, 255, 0.9) !important; /* 与App.vue保持一致 */
    transform: scale(0.95) !important;
  }
}

/* 标题栏 */
.mobile-header {
  height: 50px;
  background-color: #304156; /* 与App.vue中的.mobile-drawer背景色保持一致 */
  background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0.05), transparent); /* 添加与抽屉菜单相同的渐变效果 */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08); /* 添加与抽屉菜单相同的底部边框 */
  transition: all 0.3s ease;
  
  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: #ffffff;
    margin: 0;
  }
  
  /* 隐藏状态 */
  &.header-hidden {
    transform: translateY(-100%);
    opacity: 0;
  }
}

/* 卡片容器 */
.article-cards-container {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  padding-bottom: 20px;
  padding-top: 60px; /* 为固定定位的头部预留空间 */
  box-sizing: border-box;
  scrollbar-width: none;
  -ms-overflow-style: none;
  touch-action: pan-y; /* 允许纵向滚动，减少横向手势与系统返回冲突 */
  transition: padding-top 0.3s ease; /* 添加顶部内边距的过渡动画 */
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* 当头部隐藏时，减少顶部内边距 */
  &.header-hidden {
    padding-top: 10px;
  }
  
  /* 文章卡片 */
  .article-card {
    background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    margin-bottom: 16px;
    overflow: hidden;
    transition: all 0.3s ease;
    width: 100%;
    display: flex; /* 使用flex布局 */
    align-items: flex-start; /* 对齐卡片顶部 */
    position: relative; /* 相对定位 */
    border: 1px solid rgba(235, 238, 245, 0.6);
    transform: translateX(0);
    
    &:active {
      transform: scale(0.98);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    }
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
      
      .publish-status-stamp {
        .stamp-content {
          &.published {
            background-color: rgba(103, 194, 58, 0.8);
            transform: scale(1.05);
          }
          
          &.hidden {
            background-color: rgba(245, 108, 108, 0.8);
            transform: scale(1.05);
          }
        }
      }
    }
    
    &:last-child {
      margin-bottom: 20px; /* 最后一张卡片底部增加间距 */
    }
    
    /* 置顶标识 */
    .top-indicator {
      position: absolute;
      top: 0;
      left: 0;
      background: linear-gradient(135deg, #ff6b6b, #ee5a24);
      color: white;
      padding: 6px 10px;
      font-size: 10px;
      border-radius: 0 0 12px 0;
      display: flex;
      align-items: center;
      gap: 3px;
      z-index: 2;
      font-weight: 600;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      
      i {
        font-size: 10px;
      }
    }
    
    /* 发布状态印章 */
    .publish-status-stamp {
      position: absolute;
      top: 55px;
      right: 20%;
      transform: translateY(-50%) rotate(-15deg);
      pointer-events: none;
      z-index: 2;
      
      .stamp-content {
        width: 60px;
        height: 32px;
        border-radius: 16px;
        color: white;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 14px;
        font-weight: bold;
        border: 2px solid rgba(255, 255, 255, 0.6);
        box-shadow: 0 0 8px rgba(0, 0, 0, 0.15);
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        
        &.published {
          background-color: rgba(103, 194, 58, 0.6);
        }
        
        &.hidden {
          background-color: rgba(245, 108, 108, 0.6);
        }
      }
    }

    .article-card-main {
      padding: 15px;
      padding-top: 10px;
      padding-right: 50px; /* 为状态气泡留出空间 */
      padding-bottom: 8px; /* 减少底部空白 */
      flex: 1; /* 占据剩余空间 */
      width: 100%; /* 确保内容区域占满宽度 */
      
      .article-header {
        display: flex;
        align-items: flex-start; /* 顶部对齐 */
        margin-bottom: 12px;
        min-height: 42px; /* 确保即使名称短也有足够空间 */
        padding-right: 0px; /* 为右上角三角形留出更多空间 */
        
        &.with-top-indicator {
          padding-left: 0; /* 改为由内部title-row控制 */
        }
        
        .title-row {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
        }
        .top-pill {
          height: 20px;
          line-height: 20px;
          padding: 0 8px;
          border-radius: 4px;
          background: transparent;
          color: #1f6feb;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.3px;
          border: 1px solid #1f6feb;
          white-space: nowrap;
          pointer-events: none;
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .top-pill .top-emoji { color: #1f6feb; font-size: 14px; }
        .article-name {
          font-size: 16px;
          font-weight: 600;
          color: #2c3e50;
          line-height: 1.4;
          word-break: break-word;
          margin: 0; /* 移除默认margin */
          padding: 0; /* 移除默认padding */
          flex: 1; /* 让文章名称占据更多空间 */
          display: -webkit-box;
          -webkit-line-clamp: 2; /* 最多显示2行 */
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
      
      /* 分割线 */
      .article-divider {
        height: 1px;
        background: linear-gradient(to right, transparent, #e4e7ed, transparent);
        margin: 8px 0 12px 0;
        border-radius: 1px;
      }
      
      .article-date {
        font-size: 12px;
        color: #7f8c8d;
        display: flex;
        align-items: center;
        margin-bottom: 0; /* 与标签同行，去除额外间距 */
        font-weight: 500;
        
        i {
          margin-right: 5px;
          font-size: 13px;
          color: #95a5a6;
        }
      }
      
      /* 新增：时间+标签 同行布局容器 */
      .article-meta-row {
        display: flex;
        align-items: center;
        justify-content: space-between; /* 左标签-右日期 */
        gap: 10px;
        margin-bottom: 6px; /* 减少行底部间距 */
        min-height: 22px; /* 对齐行高 */
      }
      
      .article-tags-container {
        display: flex;
        align-items: center; /* 与时间垂直对齐 */
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 0; /* 与日期同行时去掉顶部间距 */
        margin-left: 0; /* 与标题左对齐 */
        margin-right: 0;
        margin-bottom: 0;
        min-height: 20px; /* 与标签高度一致，保持行内对齐 */
        
        .article-tag {
          font-size: 10px;
          padding: 2px 6px;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 20px;
          line-height: 1;
          border-radius: 4px;
          font-weight: 500;
          
          ::v-deep .el-tag {
            border: none !important;
            background-color: var(--el-tag-bg-color) !important;
            color: white !important;
            text-align: center !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            height: 20px !important;
            line-height: 1 !important;
            border-radius: 4px !important;
            font-weight: 500 !important;
          }
        }
      }

      /* 始终将日期推到行的最右侧，并与标签垂直居中对齐 */
      .article-meta-row .article-date {
        margin-left: auto;
        display: flex;
        align-items: center;
      }
      
      /* 文章类型三角形 */
      .article-type-triangle {
        position: absolute;
        top: 0;
        right: 0;
        width: 60px;
        height: 60px;
        overflow: hidden;
        z-index: 1;
        pointer-events: none; /* 确保点击事件透过指示器 */
        
        .triangle-background {
          position: absolute;
          top: 0;
          right: 0;
          width: 0;
          height: 0;
          border-style: solid;
          border-width: 0 60px 60px 0;
          opacity: 0.85; /* 稍微降低不透明度，使其更和谐 */
        }
        
        .article-type-value {
          position: absolute;
          /* 调整位置确保完全居中显示 */
          top: 15px;
          right: 8px;
          /* 使文本平行于斜边 */
          transform: rotate(45deg);
          color: white;
          font-size: 12px;
          font-weight: bold;
          text-align: center;
          white-space: nowrap;
          width: 30px; /* 调整宽度确保文本居中 */
          line-height: 1;
          text-shadow: 0 1px 1px rgba(0,0,0,0.3);
        }
      }
    }
    
    /* 右滑操作区域 */
    .swipe-actions {
      position: absolute;
      top: 80px; /* 向下移动，避免遮盖印章 */
      right: -250px; /* 初始隐藏，增加宽度以容纳3个按钮（按钮内容226px居中，需多留 margin） */
      width: 250px;
      height: calc(100% - 80px); /* 调整高度，减去顶部偏移 */
      background: linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.1));
      display: flex;
      align-items: center;
      justify-content: center;
      transition: right 0.3s ease;
      z-index: 1;

      &.show {
        right: 0;
      }

      .action-buttons {
        display: flex;
        flex-direction: row; /* 改为横向显示 */
        gap: 8px;
        padding: 10px;

        .action-btn {
          font-size: 12px;
          padding: 8px 12px;
          border-radius: 6px;
          white-space: nowrap;
          width: 70px; /* 统一宽度 */
          height: 32px; /* 统一高度 */
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
    }
  }
  
  /* 暂无数据样式 */
  .no-data {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 300px;
    
    .no-data-content {
      text-align: center;
      padding: 30px;
      
      i {
        font-size: 50px;
        color: #dcdfe6;
        margin-bottom: 20px;
      }
      
      .no-data-title {
        font-size: 18px;
        color: #606266;
        font-weight: 500;
        margin-bottom: 10px;
        display: block;
      }
      
      .no-data-tips {
        font-size: 14px;
        color: #909399;
      }
    }
  }
  
  /* 加载更多区域 */
  .load-more, .loading-more, .no-more {
    text-align: center;
    margin-top: 10px;
    padding: 10px 0;
    
    .el-button {
      font-size: 14px;
    }
  }
  
  .loading-more {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 5px;
    color: #909399;
    
    i {
      font-size: 16px;
    }
  }
  
  .no-more {
    text-align: center;
    padding-bottom: 20px;
    
    span {
      color: #909399;
      font-size: 12px;
    }
    
    .copyright-info {
      text-align: center;
      width: 100%;
      margin: 15px auto 0;
      display: block;
      padding: 0 15px 15px;
      box-sizing: border-box;
      
      .mobile-copyright {
        text-align: center;
        width: 100%;
        color: #909399;
        font-size: 12px;
        line-height: 1.5;
      }
    }
  }
}

/* 移动端统计网格 */
.mobile-statistics-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  padding: 2px 0;
  max-height: none;
  overflow-y: visible;
  
  .stat-item {
    background-color: #f9fafc;
    border-radius: 8px;
    padding: 8px 5px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    min-height: 65px;
    height: auto;
    
    &:active {
      transform: scale(0.98);
    }
    
    /* 给不同卡片添加渐变背景色 */
    &:nth-child(1) {
      background: linear-gradient(to top, 
        rgba(226, 206, 186, 0.3) 0%, 
        rgba(226, 206, 186, 1) 100%);
      .stat-label, i { color: #8D7B64; }
      .stat-value { color: #6D5B44; }
    }
    &:nth-child(2) {
      background: linear-gradient(to top, 
        rgba(209, 178, 164, 0.3) 0%, 
        rgba(209, 178, 164, 1) 100%);
      .stat-label, i { color: #956B5A; }
      .stat-value { color: #754B3A; }
    }
    &:nth-child(3) {
      background: linear-gradient(to top, 
        rgba(190, 206, 185, 0.3) 0%, 
        rgba(190, 206, 185, 1) 100%);
      .stat-label, i { color: #5B7155; }
      .stat-value { color: #3B5135; }
    }
    &:nth-child(4) {
      background: linear-gradient(to top, 
        rgba(199, 188, 205, 0.3) 0%, 
        rgba(199, 188, 205, 1) 100%);
      .stat-label, i { color: #6B5873; }
      .stat-value { color: #4B3853; }
    }
    
    .stat-value {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 5px;
      text-align: center;
      line-height: 1.2;
    }
    
    .stat-label {
      font-size: 11px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      i {
        margin-right: 3px;
        font-size: 13px;
      }
    }
  }
}

/* 调整弹窗样式以适应统计卡片 */
.statistics-dialog-body {
  max-height: none;
  overflow-y: visible;
  padding: 12px 10px;
}

/* 自定义统计对话框标题 */
.dialog-custom-title {
  font-size: 16px;
  text-align: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

/* 所有对话框共有样式 */
::v-deep .statistics-dialog,
::v-deep .filter-dialog {
  margin-top: 10vh !important;
  background-color: #ffffff !important;
  border-radius: 8px !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3) !important;
  overflow: hidden !important;
  
  /* 移除对话框头部 */
  .el-dialog__header {
    display: none !important;
  }
  
  /* 确保内容区域上下padding一致 */
  .el-dialog__body {
    padding: 20px !important; /* 确保上下左右间距一致 */
    max-height: 75vh !important;
    overflow-y: hidden !important; /* 不显示滚动条 */
    background-color: #ffffff !important;
  }
  
  .el-dialog__headerbtn {
    z-index: 10 !important;
    top: 10px !important;
    right: 10px !important;
  }
}

/* 确保对话框没有额外的边框和内边距 */
::v-deep .el-dialog {
  background-color: #ffffff !important;
  padding: 0 !important;
  border: none !important;
}

/* 遮罩层样式 */
::v-deep .v-modal {
  opacity: 0.7 !important;
  background-color: #000000 !important;
}

/* 对话框内容样式 */
.dialog-content {
  padding: 5px;
  background-color: #ffffff !important; /* 确保内容区域是白色 */
  
  &.no-title {
    padding-top: 0; /* 移除标题时减少顶部padding */
  }
  
  .dialog-custom-title {
    font-size: 18px;
    font-weight: 600;
    color: #303133;
    margin: 0 0 15px 0;
    padding-bottom: 10px;
    border-bottom: 1px solid #ebeef5;
    text-align: center;
  }
  
  .dialog-footer {
    text-align: right;
    margin-top: 15px;
    padding-top: 10px;
    border-top: 1px solid #ebeef5;
  }
}

/* 自定义弹窗样式 */
.dialog-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2000;
  display: flex;
  justify-content: center;
}

.dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 2001;
}

.custom-dialog {
  position: absolute;
  z-index: 2002;
  width: 95%;
  max-height: 80vh;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

/* 统计弹窗：与PlanMobile一致的尺寸与padding */
.custom-dialog.stats-dialog {
  height: auto;
  max-height: none;
  width: 92%;
  max-width: 520px;
}
.custom-dialog.stats-dialog .dialog-body {
  max-height: none;
  overflow-y: visible;
  padding: 12px 10px;
}

.dialog-close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.1);
  color: #909399;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  z-index: 10;
}

.dialog-body {
  padding: 20px;
  max-height: 80vh;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  &.filter-dialog-body {
    padding: 15px 15px 10px;
  }
}

/* 查询弹窗专用样式 */
.filter-dialog-content {
  padding: 0;
  
  .filter-title {
    font-size: 16px;
    margin: 0 0 10px 0;
    padding-bottom: 8px;
  }
}

.filter-form {
  .el-form-item {
    margin-bottom: 10px;
    
    .el-form-item__label {
      padding-right: 5px;
      line-height: 32px;
    }
    
    .el-form-item__content {
      line-height: 32px;
    }
  }
  
  ::v-deep .el-input__inner,
  ::v-deep .el-select .el-input__inner {
    height: 32px;
    line-height: 32px;
  }
}

.filter-footer {
  margin-top: 10px;
  padding-top: 8px;

  .el-button {
    padding: 7px 15px;
  }
}

/* 密码弹窗样式（msgbox 方式） */
.pwd-msgbox-content {
  padding: 5px 0;
  .pwd-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 15px;
    padding-top: 5px;
  }
  .pwd-label {
    font-size: 13px;
    color: #606266;
    flex-shrink: 0;
  }
  .pwd-icon-btn {
    cursor: pointer;
    font-size: 16px;
    transition: color 0.2s;
    color: #909399;
    &:hover {
      color: #16988C !important;
    }
  }
  .pwd-btn-row {
    display: flex;
    gap: 10px;
    padding-top: 15px;
    border-top: 1px solid #ebeef5;
  }
}

/* 密码按钮样式 - 有密码（深色 teal） */
::v-deep .password-btn-filled {
  background-color: #16988C !important;
  border-color: #16988C !important;
  color: white !important;

  &:hover, &:focus {
    background-color: #1aad9e !important;
    border-color: #1aad9e !important;
    color: white !important;
  }

  &:active {
    background-color: #12807a !important;
    border-color: #12807a !important;
    color: white !important;
  }
}

/* 密码按钮样式 - 无密码（浅色 teal） */
::v-deep .password-btn-outline {
  background-color: #5dc9bf !important;
  border-color: #5dc9bf !important;
  color: white !important;

  &:hover, &:focus {
    background-color: #79d5cb !important;
    border-color: #79d5cb !important;
    color: white !important;
  }

  &:active {
    background-color: #4bb8af !important;
    border-color: #4bb8af !important;
    color: white !important;
  }
}
</style> 